/**
 * Google Analytics 4 Hook for React Router
 * 
 * This hook tracks page views automatically when the route changes.
 * It uses the gtag.js library loaded in index.html.
 * 
 * Measurement ID: G-Y6QMM052Z9
 * 
 * Usage: Call useGoogleAnalytics() inside a component that has access to React Router's location.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Google Analytics Measurement ID
const GA_MEASUREMENT_ID = 'G-Y6QMM052Z9';

// Enable debug mode for GA4 DebugView (set to false in production after testing)
const GA4_DEBUG_MODE = true;

/**
 * Helper to check if gtag is ready and GA4 is initialized
 */
const isGA4Ready = () => {
  if (typeof window.gtag !== 'function') {
    console.warn('[GA4] gtag function not available');
    return false;
  }
  return true;
};

/**
 * Send a page view event to Google Analytics 4
 * @param {string} path - The page path to track
 * @param {string} title - The page title (optional)
 */
export const trackPageView = (path, title = document.title) => {
  if (!isGA4Ready()) return;
  
  const payload = {
    page_path: path,
    page_title: title,
    debug_mode: GA4_DEBUG_MODE
  };
  
  console.log('[GA4] Sending page_view:', payload);
  window.gtag('config', GA_MEASUREMENT_ID, payload);
};

/**
 * Send a custom event to Google Analytics 4
 * @param {string} eventName - The name of the event
 * @param {object} eventParams - Additional event parameters
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (!isGA4Ready()) return;
  
  const payload = {
    ...eventParams,
    send_to: GA_MEASUREMENT_ID,
    debug_mode: GA4_DEBUG_MODE
  };
  
  console.log(`[GA4] Sending ${eventName}:`, payload);
  window.gtag('event', eventName, payload);
};

/**
 * Convert product to GA4 item format
 * @param {object} product - Product object from cart/catalog
 * @param {number} quantity - Quantity of the item
 * @returns {object} GA4 item format
 */
const formatGA4Item = (product, quantity = 1) => {
  return {
    item_id: String(product.id || product._id || product.product_id || ''),
    item_name: String(product.name || ''),
    item_category: String(product.category || ''),
    price: parseFloat(product.price) || 0,
    quantity: parseInt(quantity) || 1
  };
};

/**
 * Track add_to_cart event
 * @param {object} product - Product being added
 * @param {number} quantity - Quantity added
 */
export const trackAddToCart = (product, quantity = 1) => {
  if (!isGA4Ready()) return;
  
  const item = formatGA4Item(product, quantity);
  const value = item.price * item.quantity;
  
  const payload = {
    send_to: GA_MEASUREMENT_ID,
    debug_mode: GA4_DEBUG_MODE,
    currency: 'MXN',
    value: value,
    items: [item]
  };
  
  console.log('[GA4] Sending add_to_cart:', JSON.stringify(payload, null, 2));
  window.gtag('event', 'add_to_cart', payload);
};

/**
 * Track remove_from_cart event
 * @param {object} product - Product being removed
 * @param {number} quantity - Quantity removed
 */
export const trackRemoveFromCart = (product, quantity = 1) => {
  if (!isGA4Ready()) return;
  
  const item = formatGA4Item(product, quantity);
  const value = item.price * item.quantity;
  
  const payload = {
    send_to: GA_MEASUREMENT_ID,
    debug_mode: GA4_DEBUG_MODE,
    currency: 'MXN',
    value: value,
    items: [item]
  };
  
  console.log('[GA4] Sending remove_from_cart:', JSON.stringify(payload, null, 2));
  window.gtag('event', 'remove_from_cart', payload);
};

/**
 * Track begin_checkout event
 * @param {object} cart - Cart object with items and total
 */
export const trackBeginCheckout = (cart) => {
  if (!isGA4Ready()) return;
  if (!cart?.items?.length) {
    console.warn('[GA4] begin_checkout skipped - empty cart');
    return;
  }
  
  const items = cart.items.map(item => formatGA4Item(item, item.quantity));
  const value = parseFloat(cart.total) || 0;
  
  const payload = {
    send_to: GA_MEASUREMENT_ID,
    debug_mode: GA4_DEBUG_MODE,
    currency: 'MXN',
    value: value,
    items: items
  };
  
  console.log('[GA4] Sending begin_checkout:', JSON.stringify(payload, null, 2));
  window.gtag('event', 'begin_checkout', payload);
};

/**
 * Track purchase event - called after successful payment
 * @param {object} orderData - Order data containing transaction info
 * @param {array} items - Array of purchased items in GA4 format
 */
export const trackPurchase = (orderData, items = []) => {
  if (!isGA4Ready()) return;
  
  // Generate transaction ID
  const transactionId = String(orderData.transaction_id || orderData.order_id || orderData.id || `order_${Date.now()}`);
  
  // Prevent duplicate tracking
  const purchaseKey = `ga4_purchase_${transactionId}`;
  if (sessionStorage.getItem(purchaseKey)) {
    console.log('[GA4] Purchase already tracked:', purchaseKey);
    return;
  }
  
  // Calculate value - handle Stripe amount_total (in cents) vs regular total
  let value = 0;
  if (orderData.amount_total) {
    value = parseFloat(orderData.amount_total) / 100;
  } else {
    value = parseFloat(orderData.value || orderData.total) || 0;
  }
  
  const payload = {
    send_to: GA_MEASUREMENT_ID,
    debug_mode: GA4_DEBUG_MODE,
    transaction_id: transactionId,
    value: value,
    currency: 'MXN',
    items: items.map(item => ({
      item_id: String(item.item_id || ''),
      item_name: String(item.item_name || ''),
      item_category: String(item.item_category || ''),
      price: parseFloat(item.price) || 0,
      quantity: parseInt(item.quantity) || 1
    }))
  };
  
  // Add optional fields if present
  if (orderData.tax) payload.tax = parseFloat(orderData.tax);
  if (orderData.shipping) payload.shipping = parseFloat(orderData.shipping);
  if (orderData.coupon) payload.coupon = String(orderData.coupon);
  
  console.log('[GA4] Sending purchase:', JSON.stringify(payload, null, 2));
  window.gtag('event', 'purchase', payload);
  
  // Mark as tracked to prevent duplicates
  sessionStorage.setItem(purchaseKey, 'true');
  
  // Verify the event was queued in dataLayer
  if (window.dataLayer) {
    console.log('[GA4] dataLayer length after purchase:', window.dataLayer.length);
  }
};

/**
 * Track add_payment_info event
 * @param {string} paymentType - Payment method type (card, bank_transfer, cash_on_delivery)
 * @param {object} cart - Cart object
 */
export const trackAddPaymentInfo = (paymentType, cart) => {
  if (!isGA4Ready()) return;
  if (!cart?.items?.length) {
    console.warn('[GA4] add_payment_info skipped - empty cart');
    return;
  }
  
  const items = cart.items.map(item => formatGA4Item(item, item.quantity));
  const value = parseFloat(cart.total) || 0;
  
  const payload = {
    send_to: GA_MEASUREMENT_ID,
    debug_mode: GA4_DEBUG_MODE,
    currency: 'MXN',
    value: value,
    payment_type: String(paymentType),
    items: items
  };
  
  console.log('[GA4] Sending add_payment_info:', JSON.stringify(payload, null, 2));
  window.gtag('event', 'add_payment_info', payload);
};

/**
 * Track view_item event - for product detail pages
 * @param {object} product - Product being viewed
 */
export const trackViewItem = (product) => {
  if (!isGA4Ready()) return;
  if (!product) return;
  
  const item = formatGA4Item(product, 1);
  
  const payload = {
    send_to: GA_MEASUREMENT_ID,
    debug_mode: GA4_DEBUG_MODE,
    currency: 'MXN',
    value: item.price,
    items: [item]
  };
  
  console.log('[GA4] Sending view_item:', JSON.stringify(payload, null, 2));
  window.gtag('event', 'view_item', payload);
};

/**
 * Hook to automatically track page views on route changes
 * Must be used inside a component wrapped by BrowserRouter
 */
const useGoogleAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view whenever the location changes
    // This handles both initial load and navigation between pages
    trackPageView(location.pathname + location.search);
  }, [location]);

  return {
    trackPageView,
    trackEvent,
    trackAddToCart,
    trackRemoveFromCart,
    trackBeginCheckout,
    trackPurchase,
    trackAddPaymentInfo,
    trackViewItem,
  };
};

export default useGoogleAnalytics;
